import * as React from 'react';
import SearchField from './SearchField';
import { useEffect, useRef, useState } from 'react';
import moment from 'moment-timezone';
import { PERIOD_GROUPS, PeriodKey, periodRange } from './PeriodFilter';

/*
 * FilterMenu — the Visitors-style structured filter: a filter-icon
 * trigger opens a category list ("Filter by"); choosing a category
 * drills into a searchable value list; picking a value applies an
 * `<dimension> is <value>` filter. Applied filters render as dashed
 * chips next to the trigger (see ActivationsV2). Dimensions are
 * data-driven so new ones (tags, status, …) plug in as endpoints land.
 */

export interface FilterDimension {
    key: string;
    label: string;
    // Required: every dimension row shows its icon (and chevron) in the menu.
    icon: React.ReactNode;
    values: string[];
    /* custom drill pane (e.g. a date range) — rendered instead of the
       searchable value list; call apply() to add the filter and close */
    custom?: (
        apply: (value: string, data?: unknown) => void,
    ) => React.ReactNode;
}

export interface ActiveFilter {
    key: string;
    label: string;
    value: string;
    /* machine-readable payload for custom dimensions (value is display) */
    data?: unknown;
}

interface FilterMenuProps {
    dimensions: FilterDimension[];
    onAdd: (filter: ActiveFilter) => void;
}

export const IconFilterDate = (
    <svg
        width="14"
        height="14"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <rect x="2" y="3.2" width="12" height="10.6" rx="1.6" />
        <path d="M2 6.4h12M5.2 1.8v2.6M10.8 1.8v2.6" />
    </svg>
);

/*
 * DateRangeDrill — the shared Date drill pane: period presets first
 * (same groups as the old period pill), then a custom from/to range
 * (either side optional). data = {from, to} as YYYY-MM-DD or null.
 */
export function DateRangeDrill({
    apply,
}: {
    apply: (value: string, data?: unknown) => void;
}) {
    const [customOpen, setCustomOpen] = useState(false);
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');

    const pickPreset = (key: PeriodKey, label: string) => {
        const range = periodRange(key);
        apply(label, {
            from: range ? range[0].format('YYYY-MM-DD') : null,
            to: range ? range[1].format('YYYY-MM-DD') : null,
        });
    };

    if (!customOpen) {
        return (
            <div className="max-h-[300px] overflow-y-auto pb-1">
                {PERIOD_GROUPS.map((group, gi) => (
                    <div
                        key={gi}
                        className={
                            gi > 0 ? 'mt-1 border-t border-line pt-1' : ''
                        }
                    >
                        {group.map((option) => (
                            <button
                                key={option.key}
                                type="button"
                                onClick={() =>
                                    pickPreset(option.key, option.label)
                                }
                                className="block w-full cursor-pointer border-0 bg-transparent px-4 py-[7px] text-left font-[inherit] text-[13.5px] text-foreground hover:bg-sunk"
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                ))}
                <div className="mt-1 border-t border-line pt-1">
                    <button
                        type="button"
                        onClick={() => setCustomOpen(true)}
                        className="flex w-full cursor-pointer items-center border-0 bg-transparent px-4 py-[7px] text-left font-[inherit] text-[13.5px] font-semibold text-foreground hover:bg-sunk"
                    >
                        <span className="flex-1">Custom range</span>
                        <span aria-hidden className="text-[12px] text-faint">
                            ›
                        </span>
                    </button>
                </div>
            </div>
        );
    }

    const ready = from.length > 0 || to.length > 0;
    const submit = () => {
        if (!ready) return;
        const f = from ? moment(from) : null;
        const t = to ? moment(to) : null;
        const label =
            f && t
                ? `${f.format('MMM D')} – ${t.format('MMM D, YYYY')}`
                : f
                  ? `from ${f.format('MMM D, YYYY')}`
                  : `until ${t!.format('MMM D, YYYY')}`;
        apply(label, { from: from || null, to: to || null });
    };
    const field =
        'w-full rounded-[8px] border border-border bg-transparent px-2 py-1.5 font-[inherit] text-[13px] text-foreground';
    return (
        <div className="px-4 pb-3 pt-1">
            <div className="flex items-center gap-2">
                <div className="flex-1">
                    <div className="ps-label mb-1">From</div>
                    <input
                        type="date"
                        value={from}
                        onChange={(e) => setFrom(e.target.value)}
                        className={field}
                    />
                </div>
                <div className="flex-1">
                    <div className="ps-label mb-1">To</div>
                    <input
                        type="date"
                        value={to}
                        onChange={(e) => setTo(e.target.value)}
                        className={field}
                    />
                </div>
            </div>
            <button
                type="button"
                disabled={!ready}
                onClick={submit}
                className={
                    'mt-2.5 w-full cursor-pointer rounded-full border-0 py-2 text-center font-[inherit] text-[12.5px] font-semibold text-brand-foreground ' +
                    (ready ? 'bg-brand hover:opacity-95' : 'bg-brand/40')
                }
            >
                Apply
            </button>
        </div>
    );
}

/* shared Date dimension — same shape on every list page */
export const dateDimension: FilterDimension = {
    key: 'date',
    label: 'Date',
    icon: IconFilterDate,
    values: [],
    custom: (apply) => <DateRangeDrill apply={apply} />,
};

export default function FilterMenu({ dimensions, onAdd }: FilterMenuProps) {
    const [open, setOpen] = useState(false);
    const [drill, setDrill] = useState<FilterDimension | null>(null);
    const [query, setQuery] = useState('');
    const rootRef = useRef<HTMLDivElement>(null);

    const close = () => {
        setOpen(false);
        setDrill(null);
        setQuery('');
    };

    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            if (!rootRef.current?.contains(e.target as Node)) close();
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') close();
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const shownValues = drill
        ? drill.values.filter((v) =>
              v.toLowerCase().includes(query.trim().toLowerCase()),
          )
        : [];

    return (
        <div ref={rootRef} className="relative">
            <button
                type="button"
                title="Filter"
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => (open ? close() : setOpen(true))}
                className="ps-pill flex w-9 cursor-pointer items-center justify-center text-muted-foreground hover:text-foreground"
            >
                <svg
                    width="15"
                    height="15"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                >
                    <path d="M2 4h12M4.5 8h7M6.5 12h3" />
                </svg>
            </button>

            {open && (
                <div className="absolute right-0 top-[calc(100%+6px)] z-40 w-[320px] rounded-2xl border border-border bg-background py-2 shadow-[0_18px_50px_-12px_rgba(20,23,29,0.25)]">
                    {!drill && (
                        <>
                            <div className="px-4 pb-1 pt-1.5 text-[12.5px] text-faint">
                                Filter by
                            </div>
                            {dimensions.map((dim) => (
                                <button
                                    key={dim.key}
                                    type="button"
                                    onClick={() => setDrill(dim)}
                                    className="flex w-full cursor-pointer items-center gap-2.5 border-0 bg-transparent px-4 py-[7px] text-left font-[inherit] text-[13.5px] text-foreground hover:bg-sunk"
                                >
                                    <span className="flex text-faint">
                                        {dim.icon}
                                    </span>
                                    <span className="flex-1">{dim.label}</span>
                                    <span
                                        aria-hidden
                                        className="text-[12px] text-faint"
                                    >
                                        ›
                                    </span>
                                </button>
                            ))}
                        </>
                    )}

                    {drill && (
                        <>
                            <div className="flex items-center gap-2 px-3 pb-1.5 pt-1">
                                <button
                                    type="button"
                                    aria-label="Back"
                                    onClick={() => {
                                        setDrill(null);
                                        setQuery('');
                                    }}
                                    className="cursor-pointer border-0 bg-transparent p-1 font-[inherit] text-[13px] text-faint hover:text-foreground"
                                >
                                    ‹
                                </button>
                                <span className="text-[13px] font-semibold">
                                    {drill.label}
                                </span>
                                <span className="ml-auto pr-1 text-[12px] text-faint">
                                    is
                                </span>
                            </div>
                            {drill.custom ? (
                                drill.custom((value, data) => {
                                    onAdd({
                                        key: drill.key,
                                        label: drill.label,
                                        value,
                                        data,
                                    });
                                    close();
                                })
                            ) : (
                                <>
                                    <div className="px-3 pb-2">
                                        <SearchField
                                            autoFocus
                                            value={query}
                                            onChange={setQuery}
                                            placeholder="Search…"
                                            className="w-full"
                                        />
                                    </div>
                                    <div className="max-h-[220px] overflow-y-auto">
                                        {shownValues.map((value) => (
                                            <button
                                                key={value}
                                                type="button"
                                                onClick={() => {
                                                    onAdd({
                                                        key: drill.key,
                                                        label: drill.label,
                                                        value,
                                                    });
                                                    close();
                                                }}
                                                className="block w-full cursor-pointer truncate border-0 bg-transparent px-4 py-[7px] text-left font-[inherit] text-[13.5px] text-foreground hover:bg-sunk"
                                            >
                                                {value}
                                            </button>
                                        ))}
                                        {shownValues.length === 0 && (
                                            <div className="px-4 py-3 text-[12.5px] text-faint">
                                                No matches.
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
